import type { OrganizationSubscription } from "@arbeidsark/shared"
import styled from "styled-components"
import { OutlineButton } from "~/components"
import { useTranslate } from "~/ur-react-hooks"
import { useToast } from "~/utils"
import { format } from "~/utils/date-fns/format"
import { useErrorHandling } from "~/utils/error"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 6rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};
  padding: 2rem;
  width: 456px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 8px;
`

const Title = styled.h3`
  margin: 0;
`

const Subtitle = styled.p`
  margin: 0;
  margin-top: 1rem;
  text-align: center;
`
const Actions = styled.div`
  margin-top: 2rem;
`

export interface SubscriptionSchoolCancelledProps {
  subscription: OrganizationSubscription
}

const SubscriptionSchoolCancelled: React.FC<
  SubscriptionSchoolCancelledProps
> = ({ subscription }) => {
  const organizationMembership = useOrganizationMembership()!

  const toast = useToast()
  const trpcContext = trpc.useContext()
  const formattedExpirationDate = format(
    subscription.currentPeriodEnd ?? new Date(),
    "d. MMMM yyyy",
  )

  const translations = useTranslate({
    subtitle: [
      "subscription-school-cancelled.subtitle",
      { date: formattedExpirationDate },
    ],
    reactivateSubscription:
      "subscription-school-cancelled.reactivate-subscription",
    toastSuccess: "subscription-school-cancelled.toast.success",
  })

  const { getToastInfo } = useErrorHandling()

  const { mutate: reactivateSubscription } =
    trpc.subscriptions.reactivateOrganizationSubscription.useMutation({
      onSuccess() {
        trpcContext.subscriptions.getOrganizationSubscription.invalidate()
        toast("success", translations.toastSuccess)
      },
      onError(error) {
        const { type, message } = getToastInfo(
          error.message,
          error.data?.code,
          "error",
        )

        toast(type, message)
      },
    })

  return (
    <Layout>
      <Card>
        <Title>{organizationMembership?.organization.name}</Title>
        <Subtitle>{translations.subtitle()}</Subtitle>
        <Actions>
          <OutlineButton
            onClick={() => {
              reactivateSubscription({
                organizationId: organizationMembership.organizationId,
                organizationSubscriptionId: subscription.id,
              })
            }}
          >
            {translations.reactivateSubscription}
          </OutlineButton>
        </Actions>
      </Card>
    </Layout>
  )
}

export default SubscriptionSchoolCancelled
